import type {
  HeroSectionsByProduct,
  ProductsByHeroSection,
  SectionsType,
} from '@/components/lpg/lpgTypesAndDefinitions';
import { PRODUCT_BY_HERO_SECTION } from '@/components/lpg/lpgTypesAndDefinitions';
import type { ResponsiveConfig } from '@/components/lpg/tsHelpers';
import { useLangAndRegion } from '@/store/useLangAndRegion';
import { useTheme } from '@/store/useTheme';
import type { Locale } from '@lemonade-hq/lemonation';
import type { Language, Region } from '@lemonade-hq/lemonation';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

const ORIGINAL_PRODUCT_NAMES = {
  life: 'life',
  renters: 'renters',
  pet: 'pet',
  home: 'homeowners',
  car: 'car',
};

export type PageSection = {
  type: SectionsType; // better type this with a union of all possible section types
  props?: {
    title: string;
    subtitle: string;
    ctaLabel?: string;
    ctaUrl?: string;
    ctaLabels?: Record<ProductsByHeroSection, ResponsiveConfig<string>>;
    partnerLogo?: string;
    bundlesMainTitle?: string;
    bundlesMainImageName?: string;
    bundlesFirstTitle?: string;
    bundlesFirstImageName?: string;
    bundlesFirstHidden?: boolean;
    bundlesSecondTitle?: string;
    bundlesSecondImageName?: string;
    bundlesSecondHidden?: boolean;
    bundlesThirdTitle?: string;
    bundlesThirdImageName?: string;
    bundlesThirdHidden?: boolean;
    underCtaText?: string;
    linkInSubtitleLabel?: string;
    linkInSubtitleURL?: string;
    linkLabel?: string;
    linkURL?: string;
    priceType?: 'fixed' | 'ppm';
  };
};

export type Page = {
  url: string;
  title: string;
  locale: Locale;
  description: string;
  robotsIndex: boolean;
  robotsFollow: boolean;
  ogImage: string | null;
  status: 'published';
  partnerId: number | null;
  sections: PageSection[];
};

export type PageSectionWithLocale = PageSection & { locale: Locale };

const getProductBySectionType = (sectionType: HeroSectionsByProduct): ProductsByHeroSection =>
  PRODUCT_BY_HERO_SECTION[sectionType];

const pageViewAttributes = ({ sectionType, slug }: { sectionType: HeroSectionsByProduct; slug: string }) => {
  return {
    landing_page_generator: 'true',
    page_name: slug,
    ...(getProductBySectionType(sectionType) != null && {
      original_product: ORIGINAL_PRODUCT_NAMES[getProductBySectionType(sectionType)],
    }),
  };
};

export const useLpg = (pages: Page[]) => {
  const router = useRouter();
  const slug = router.query.slug as string;
  const { trackDataForGtm, trackEvent } = useAnalyticsContext();
  const setTheme = useTheme(state => state.setTheme);
  const setLangAndRegion = useLangAndRegion(state => state.setLangAndRegion);

  const page = useMemo(() => pages.find(page => page.url === slug), [pages, slug]);

  const locale = useMemo(
    () => (page?.locale ?? 'en-us').replace(/-\w+/, match => match.toUpperCase()) as Locale,
    [page],
  );

  const seo = useMemo(
    () => ({
      title: page?.title ?? '',
      description: page?.description ?? '',
      metaTitle: page?.title ?? '',
      ogImage: page?.ogImage ?? '',
    }),
    [page],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: only on mount
  useEffect(() => {
    trackDataForGtm(`${slug}_viewed`);
    trackEvent('landing_page.viewed', {
      ...pageViewAttributes({
        sectionType: page?.sections[0]?.type as HeroSectionsByProduct,
        slug,
      }),
    });
  }, []);

  const [lang, region] = useMemo(() => locale.split('-') as [Language, Region], [locale]);

  const theme = useMemo(() => (locale === 'de-DE' ? 'reddish' : 'pinkish'), [locale]);

  useEffect(() => {
    // setting global theme based on locale since LPG pages does not include language in the URL
    setTheme({ theme });
    setLangAndRegion({ lang, region });
  }, [lang, region, setLangAndRegion, setTheme, theme]);

  const product = useMemo(() => getProductBySectionType(page?.sections[0]?.type as HeroSectionsByProduct), [page]);

  const isCarPpm = useMemo(() => {
    if (product !== 'car' || page == null) return false;
    // if some section type includes _ppm or if some section props includes priceType property is car ppm
    return page.sections.some(section => section.type.includes('_ppm') || section.props?.priceType != null);
  }, [product, page]);

  return {
    locale,
    slug,
    page,
    seo,
    product,
    isCarPpm,
  };
};
